// Step8CarrierQuestions.tsx
import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent,
  SelectItem, SelectGroup, SelectLabel,
} from "../../../../components/ui/select";
import { limitAutoLiabilityOptionsForCoverWhaleAndNirvana, limitAutoLiabilityOptionsForProgressive } from "data";

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const COMP_COLLISION_OPTIONS = [
  { value: "na", label: "N/A" },
  { value: "1000", label: "$1,000" },
  { value: "2500", label: "$2,500" },
];

const BI_LIMIT_OPTIONS = [
  { value: "25k-50k", label: "$ 25,000 - $ 50,000" },
  { value: "50k-100k", label: "$ 50,000 - $ 100,000" },
  { value: "100k_csl", label: "$ 100,000 CSL" },
  { value: "100k-300k", label: "$ 100,000 - $ 300,000" },
  { value: "300k_csl", label: "$ 300,000 CSL" },
  { value: "250k-500k", label: "$ 250,000 - $ 500,000" },
  { value: "500k_csl", label: "$ 500,000 CSL" },
  { value: "750k_csl", label: "$ 750,000 CSL" },
  { value: "1m_csl", label: "$ 1,000,000 CSL" },
];

export function ALSection() {
  const { control, watch, setValue, register, formState } = useFormContext();

  // mirror carriers into step8.carrier so schema can validate
  const c1 = useWatch({ control, name: "step1.carrier" });
  const c8 = useWatch({ control, name: "step8.carrier" });
  const compCollisionDeductibleChecked = useWatch({ control, name: "step8.compCollisionDeductibleChecked" });
  const currentBILimitChecked = useWatch({ control, name: "step8.currentBILimitChecked" });
  const hiredAutoLiability = useWatch({ control, name: "step8.hiredAutoLiability" });
  const hasAL = watch("step8.auto_liability");

  React.useEffect(() => {
    register("step8.carrier");
  }, [register]);

  React.useEffect(() => {
    const list = Array.isArray(c1) ? c1 : c1 ? [c1] : [];
    const normalized = list.map((s) => (s ?? ""));
    if (!arraysEqual(normalized, c8 ?? [])) {
      setValue("step8.carrier", normalized, { shouldDirty: false, shouldValidate: true });
    }
  }, [c1, c8, setValue]);

  const carriers = (c8 ?? []).map((s) => (s ?? ""));
  const hasProg = carriers.includes("Progressive");
  const hasNir = carriers.includes("Nirvana");
  const hasCW = carriers.includes("Cover Whale");
  const hasAccelerant = carriers.includes("Accelerant");

  const carrierList = [
    hasCW && "Cover Whale",
    hasAccelerant && "Accelerant",
    hasNir && "Nirvana"
  ].filter(Boolean);

  const label = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(carrierList);

  return (
    <div className="space-y-3 py-1">
      {/* PROGRESSIVE ONLY: Insured + Continuous Coverage */}
      {(hasProg) && (
        <div className="space-y-3 my-6 rounded-lg border p-4">
          <div>
            <div>
              Is the customer currently insured?
              <span className="block text-xs text-slate-600">
                (Personal Auto policies also qualify as proof of prior insurance — we will need a DECLARATIONS page showing AL Limits prior to binding)
              </span>
            </div>
            <div className="flex gap-6 mt-2">
              <Controller
                control={control}
                name="step8.currentlyInsured"
                render={({ field }) => (
                  <>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="currently-insured-yes"
                        checked={field.value === "yes"}
                        onCheckedChange={(v) => field.onChange(v ? "yes" : "na")}
                      />
                      <Label htmlFor="currently-insured-yes" className="cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="currently-insured-no"
                        checked={field.value === "no"}
                        onCheckedChange={(v) => field.onChange(v ? "no" : "na")}
                      />
                      <Label htmlFor="currently-insured-no" className="cursor-pointer">No</Label>
                    </div>
                  </>
                )}
              />
            </div>
            {formState.errors?.step8?.currentlyInsured && (
              <p className="text-xs text-red-600 mt-1">{String((formState.errors.step8).currentlyInsured.message)}</p>
            )}
          </div>

          <div>
            <div>
              Has the customer had continuous coverage?
              <span className="block text-xs text-slate-600">
                (We will need a DECLARATIONS page showing AL Limits prior to binding)
              </span>
            </div>
            <div className="flex gap-6 mt-2">
              <Controller
                control={control}
                name="step8.continuousCoverage"
                render={({ field }) => (
                  <>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="continuous-yes"
                        checked={field.value === "yes"}
                        onCheckedChange={(v) => field.onChange(v ? "yes" : "na")}
                      />
                      <Label htmlFor="continuous-yes" className="cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="continuous-no"
                        checked={field.value === "no"}
                        onCheckedChange={(v) => field.onChange(v ? "no" : "na")}
                      />
                      <Label htmlFor="continuous-no" className="cursor-pointer">No</Label>
                    </div>
                  </>
                )}
              />
            </div>
            {formState.errors?.step8?.continuousCoverage && (
              <p className="text-xs text-red-600 mt-1">{String((formState.errors.step8).continuousCoverage.message)}</p>
            )}
          </div>
        </div>
      )}
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Controller
            control={control}
            name="step8.auto_liability"
            render={({ field }) => (
              <Checkbox
                id="cov-al"
                checked={!!field.value}
                onCheckedChange={(v) => {
                  field.onChange(v);
                  if (v) {
                    // default ON and reset limit to “unselected”
                    setValue("step8.personal_injury_protection", true);
                    setValue("step8.uninsured_motorists_property_damage", true);
                    setValue("step8.auto_liability_limit", "unselected");
                  } else {
                    setValue("step8.personal_injury_protection", false);
                    setValue("step8.uninsured_motorists_property_damage", false);
                    setValue("step8.auto_liability_limit", undefined);
                  }
                }}
              />
            )}
          />
          <Label htmlFor="cov-al" className="cursor-pointer">
            Auto Liability (AL)
          </Label>
        </div>
        {hasAL && (
          <>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="step8.personal_injury_protection"
                render={({ field }) => (
                  <Checkbox id="cov-pip" checked={!!field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="cov-pip" className="cursor-pointer">
                Personal Injury Protection (PIP)
              </Label>
            </div>

            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="step8.uninsured_motorists_property_damage"
                render={({ field }) => (
                  <Checkbox id="cov-um" checked={!!field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="cov-um" className="cursor-pointer">
                Uninsured Motorists/Property Damage
              </Label>
            </div>
          </>
        )}

        {hasAL && (hasCW || hasNir || hasAccelerant) && (
          <div className="space-y-3 pt-1">
            <Label>
              Auto Liability Limit for {label} <span className="text-red-600">*</span>
            </Label>
            <Controller
              control={control}
              name="step8.auto_liability_limit_for_cover_whale_and_nirvana"
              render={({ field, fieldState }) => {
                return (
                  <>
                    <Select value={(field.value) ?? "unselected"} onValueChange={field.onChange} onOpenChange={() => field.onChange(field.value)}>
                      <SelectTrigger className="w-full !h-10 py-2 px-3">
                        <SelectValue placeholder="Please select an option" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        <SelectGroup>
                          <SelectLabel>Limits</SelectLabel>
                          {/* keep this explicit item for UX parity */}
                          <SelectItem value="unselected">Please select an option</SelectItem>
                          {limitAutoLiabilityOptionsForCoverWhaleAndNirvana.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {fieldState.error && (
                      <p className="text-xs text-red-600 mt-1">{fieldState.error.message}</p>
                    )}
                  </>
                )
              }}
            />
          </div>
        )}
        {hasAL && (hasProg) && (
          <div className="space-y-3 pt-1">
            <Label>
              Auto Liability Limit for Progressive <span className="text-red-600">*</span>
            </Label>
            <Controller
              control={control}
              name="step8.auto_liability_limit_for_progressive"
              render={({ field, fieldState }) => (
                <>
                  <Select value={(field.value) ?? "unselected"} onValueChange={field.onChange} onOpenChange={() => field.onChange(field.value)}>
                    <SelectTrigger className="w-full !h-10 py-2 px-3">
                      <SelectValue placeholder="Please select an option" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      <SelectGroup>
                        <SelectLabel>Limits</SelectLabel>
                        {/* keep this explicit item for UX parity */}
                        <SelectItem value="unselected">Please select an option</SelectItem>
                        {limitAutoLiabilityOptionsForProgressive.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {fieldState.error && (
                    <p className="text-xs text-red-600 mt-1">{fieldState.error.message}</p>
                  )}
                </>
              )}
            />
          </div>
        )}
      </div>
      {/* PROGRESSIVE or NIRVANA: Comp/Collision Deductible */}
      {(hasProg || hasNir) && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="step8.compCollisionDeductibleChecked"
                render={({ field }) => (
                  <Checkbox
                    id="comp-coll"
                    checked={!!field.value}
                    onCheckedChange={(v) => {
                      field.onChange(v);
                      if (!v) {
                        setValue("step8.compCollisionDeductibleOption", undefined, { shouldDirty: true, shouldValidate: true });
                      }
                    }}
                  />
                )}
              />
              <Label htmlFor="comp-coll" className="cursor-pointer">
                Comp/Collision Deductible
              </Label>
            </div>

            {compCollisionDeductibleChecked && <Controller
              control={control}
              name="step8.compCollisionDeductibleOption"
              render={({ field, fieldState }) => (
                <>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                    disabled={!compCollisionDeductibleChecked}
                  >
                    <SelectTrigger className="w-full !h-10 py-2 px-3">
                      <SelectValue placeholder="Select a deductible" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {COMP_COLLISION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldState.error && <p className="text-xs text-red-600 mt-1">{fieldState.error.message}</p>}
                </>
              )}
            />}
          </div>
        </div>
      )}
      {/* PROGRESSIVE ONLY: Current BI Limit */}
      {hasProg && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex flex-col space-y-3">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="step8.currentBILimitChecked"
                render={({ field }) => (
                  <Checkbox
                    id="bi-limit-chk"
                    checked={!!field.value}
                    onCheckedChange={(v) => {
                      field.onChange(v);
                      if (!v) setValue("step8.currentBILimitValue", undefined, { shouldDirty: true, shouldValidate: true });
                    }}
                  />
                )}
              />
              <Label htmlFor="bi-limit-chk" className="cursor-pointer">
                Current Bodily Injury Liability Limit
              </Label>
            </div>

            {currentBILimitChecked && <Controller
              control={control}
              name="step8.currentBILimitValue"
              render={({ field, fieldState }) => (
                <>
                  <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={!currentBILimitChecked}>
                    <SelectTrigger className="w-full !h-10 py-2 px-3">
                      <SelectValue placeholder="Select a BI limit" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {BI_LIMIT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldState.error && <p className="text-xs text-red-600 mt-1">{fieldState.error.message}</p>}
                </>
              )}
            />}
          </div>
        </div>
      )}
      {/* PROGRESSIVE ONLY: Rental & Roadside (checked → send "yes") */}
      {(hasProg || hasAccelerant) && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step8.rentalReimbursement"
              render={({ field }) => (
                <Checkbox
                  id="rental-reimb"
                  checked={field.value === "yes"}
                  onCheckedChange={(v) => field.onChange(v ? "yes" : "na")}
                />
              )}
            />
            <Label htmlFor="rental-reimb" className="cursor-pointer">Rental Reimbursement</Label>
          </div>
        </div>
      )}
      {/* PROGRESSIVE ONLY: Rental & Roadside (checked → send "yes") */}
      {(hasProg || hasAccelerant) && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step8.roadsideAssistance"
              render={({ field }) => (
                <Checkbox
                  id="roadside"
                  checked={field.value === "yes"}
                  onCheckedChange={(v) => field.onChange(v ? "yes" : "na")}
                />
              )}
            />
            <Label htmlFor="roadside" className="cursor-pointer">Roadside Assistance</Label>
          </div>
        </div>
      )}
      {/* PROGRESSIVE ONLY: Hired Auto Liability */}
      {(hasProg || hasAccelerant)  && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="step8.hiredAutoLiability"
                render={({ field }) => (
                  <Checkbox
                    id="hired-auto"
                    checked={!!field.value}
                    onCheckedChange={(v) => {
                      field.onChange(v);
                      if (!v) setValue("step8.hiredAutoCount", undefined, { shouldDirty: true, shouldValidate: true });
                    }}
                  />
                )}
              />
              <Label htmlFor="hired-auto" className="cursor-pointer">Hired Auto Liability</Label>
            </div>

            {/* number input when checked */}
            {hiredAutoLiability && (
              <div className="max-w-xs">
                <Label className="text-sm">How many autos did the customer rent, hire, or borrow in the last year?</Label>
                <Controller
                  control={control}
                  name="step8.hiredAutoCount"
                  render={({ field, fieldState }) => (
                    <>
                      <Input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                      />
                      {fieldState.error && <p className="text-xs text-red-600 mt-1">{fieldState.error.message}</p>}
                    </>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      )}
      {/* PROGRESSIVE ONLY: Blanket AI (checked → send "yes") */}
      {(hasProg || hasAccelerant)  && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Controller
              control={control}
              name="step8.blanketAdditionalInsured"
              render={({ field }) => (
                <Checkbox
                  id="blanket-ai"
                  checked={field.value === "yes"}
                  onCheckedChange={(v) => field.onChange(v ? "yes" : "na")}
                />
              )}
            />
            <Label htmlFor="blanket-ai" className="cursor-pointer">
              Is a Blanket Additional Insured endorsement needed by contract?
            </Label>
          </div>
        </div>
      )}
    </div>
  );
}
